/**
 * component-predictive-search.js
 * Live results inside the header search panel.
 *
 * Pure enhancement. The search form is a real GET to /search and works
 * without this file; it only replaces "type, submit, wait for a page load"
 * with "type, see results".
 *
 * Markup comes back rendered from sections/predictive-search.liquid rather
 * than being built here, so results reuse the same cards as the rest of the
 * site and there is one implementation to maintain.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 250;
  const MIN_LENGTH = 2;

  const input = document.querySelector('[data-predictive-input]');
  const output = document.querySelector('[data-predictive-results]');
  if (!input || !output) return;

  let controller = null;

  /**
   * Renders results by adopting an already-parsed node rather than assigning
   * an HTML string.
   *
   * The markup is our own Liquid output and is escaped at the source, so a
   * string assignment would not actually be exploitable — but it would leave
   * the theme's only HTML-string-injection pattern in the one file that
   * handles user-typed input. Adopting the node also avoids a second parse.
   *
   * The container is a live region declared in the markup. Announcing on every
   * keystroke would talk over the user as they type, so the section renders a
   * visually-hidden result count and only that settled value is announced.
   *
   * @param {Element|null} node Parsed results element, or null to clear.
   */
  function render(node) {
    output.replaceChildren();
    if (node) output.appendChild(document.importNode(node, true));

    // Wire the panel's aria-expanded to whether results are actually showing,
    // so assistive tech reports the combobox state truthfully.
    input.setAttribute('aria-expanded', node ? 'true' : 'false');
  }

  function clear() {
    if (controller) controller.abort();
    output.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
  }

  function search(term) {
    if (controller) controller.abort();
    controller = new AbortController();

    const url = new URL(window.Theme.routes.predictive_search, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    // Pages and articles are included deliberately: on this store the buying
    // guides answer most pre-purchase questions, and a product-only search
    // sends "will this survive outdoors" to a grid instead of the answer.
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit]', '5');

    fetch(url.toString(), {
      signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        return response.text();
      })
      .then((html) => {
        // The section response is a full document; take only the rendered
        // results element so wrapper markup is not injected into the panel.
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        render(parsed.querySelector('.predictive'));
      })
      .catch((error) => {
        // Aborts are the normal result of a newer keystroke superseding this
        // request, not a failure worth surfacing.
        if (error.name === 'AbortError') return;
        clear();
      });
  }

  const debouncedSearch = window.Theme.debounce((term) => search(term), DEBOUNCE_MS);

  input.addEventListener('input', () => {
    const term = input.value.trim();

    // Below two characters the results are noise — every product matches.
    if (term.length < MIN_LENGTH) {
      clear();
      return;
    }

    debouncedSearch(term);
  });

  // Escape clears results before the drawer's own Escape handler closes the
  // panel, so the first press is "dismiss the suggestions" and the second is
  // "close search" — which is what the combobox pattern leads people to expect.
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!output.firstElementChild) return;
    event.stopPropagation();
    clear();
  });

  // Arrow-down moves into the results list. Without it, a keyboard user has to
  // Tab through the submit button and the suggestion chips to reach a result.
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown') return;
    const first = output.querySelector('a');
    if (!first) return;
    event.preventDefault();
    first.focus();
  });
})();
